// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, FormError, useToast, type Finding } from '@intakeform/ui';
import { RELATIONSHIP_CODES, RelationshipCode, type RelationshipCodeValue } from '@intakeform/fhir';
import { useCreateMagicLink, useMagicLinkChannels } from '../api/magic-links.js';
import { MagicLinkQr } from './MagicLinkQr.js';
import type { QuestionnaireSummary } from '../api/types.js';

export interface MagicLinkModalProps {
  forms: readonly QuestionnaireSummary[];
  /** Pre-select a form id if the caller already knows which one to issue against. */
  defaultFormId?: string;
  defaultEmail?: string;
  defaultPhone?: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

type Channel = 'email' | 'sms' | 'console';

const CHANNEL_LABELS: Record<Channel, string> = {
  email: 'Email',
  sms: 'SMS',
  console: 'Console (dev — prints to log)',
};

/**
 * What to do with the link once it exists (intakeform-preview-lmxg).
 *
 * `send` is the status quo and stays the default: for a link issued remotely,
 * nobody is at the desk to read a screen, so rendering a live credential
 * there is exposure that buys nothing. The desk case is a deliberate click.
 */
type DeliveryMode = 'send' | 'send-and-show' | 'show';

const DELIVERY_MODE_LABELS: Record<DeliveryMode, string> = {
  send: 'Send it to the patient',
  'send-and-show': 'Send it and show it here',
  show: 'Show it here only — send nothing',
};

const CONTACT_REQUIRED: Finding = {
  severity: 'error',
  code: 'required',
  message: 'Email or phone is required — provide at least one.',
};

/**
 * Issue a magic link for a patient. Uses the shared `Dialog` primitive
 * (Radix-backed) so focus trap, Escape close, scroll lock, and ARIA
 * states come for free.
 *
 * Validation (in-y2z): the API rejects when both `email` and `phone` are
 * blank with a Zod 422; we mirror the same rule client-side so the
 * submit button stays disabled and a `FormError` surfaces on blur
 * instead of the raw JSON error bubbling up through the mutation. Same
 * rule applies to the optional caregiver/filler block when the toggle
 * is on. The server stays as defence in depth.
 *
 * EMR-prefill (autofill): callers can pass `defaultEmail` / `defaultPhone`
 * / `defaultFormId` to populate the form from an EMR-derived patient
 * record. The values are synced into local state every time the modal
 * opens so a second EMR-prefilled launch doesn't carry over the
 * previous patient's data.
 */
export function MagicLinkModal({
  forms,
  defaultFormId,
  defaultEmail,
  defaultPhone,
  open,
  onOpenChange,
}: MagicLinkModalProps) {
  const [formId, setFormId] = useState<string>(defaultFormId ?? forms[0]?.id ?? '');
  // Empty until the server says what it can deliver. The browser cannot know
  // — the sender is fixed at boot from MAGIC_LINK_SENDER — and every attempt
  // to guess produced the same bug pointing a different way: a `console`
  // default production could not serve (intakeform-preview-2a0), then an
  // `email` default that broke every dev stack, which runs the console sender.
  const [channel, setChannel] = useState<Channel | ''>('');
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('send');
  /** Required (intakeform-preview-1dba) — the only field that identifies WHICH patient. */
  const [patientName, setPatientName] = useState('');
  const [patientNameTouched, setPatientNameTouched] = useState(false);
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [phone, setPhone] = useState(defaultPhone ?? '');
  /** Practice-issued chart number, when the clinic has it to hand (eif). */
  const [mrn, setMrn] = useState('');
  const [patientContactTouched, setPatientContactTouched] = useState(false);

  const [fillerEnabled, setFillerEnabled] = useState(false);
  const [fillerRelationshipCode, setFillerRelationshipCode] =
    useState<RelationshipCodeValue>('parent');
  const [fillerEmail, setFillerEmail] = useState('');
  const [fillerPhone, setFillerPhone] = useState('');
  const [fillerName, setFillerName] = useState('');
  const [fillerContactTouched, setFillerContactTouched] = useState(false);
  /** Per-link TTL override displayed in HOURS; empty = use precedence default. */
  const [ttlHoursOverride, setTtlHoursOverride] = useState('');

  const dialogRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const create = useCreateMagicLink();
  const link = create.data;
  /**
   * The link to put on screen. `link` is the one staff asked to be shown
   * (lmxg) and works in production; `devLink` is the developer convenience
   * production suppresses. Kept as separate response keys on purpose — one
   * env check must never stand between "the clinician sees what they asked
   * for" and "every issuance leaks one", so only the fallback order is
   * shared here, never the field itself.
   */
  const displayedLink = link?.link ?? link?.devLink;

  // Reset transient state when the dialog closes so reopening starts
  // fresh — without this, a previous "touched" flag would surface the
  // FormError before the user has typed anything. When opening, sync
  // the EMR-prefill defaults into local state so the patient block is
  // pre-populated for the autofill flow.
  useEffect(() => {
    if (open) {
      setPatientName('');
      setPatientNameTouched(false);
      setEmail(defaultEmail ?? '');
      setPhone(defaultPhone ?? '');
      setMrn('');
      setFormId(defaultFormId ?? forms[0]?.id ?? '');
      setPatientContactTouched(false);
      setFillerContactTouched(false);
      setFillerEnabled(false);
      setFillerEmail('');
      setFillerPhone('');
      setFillerName('');
      setTtlHoursOverride('');
      setDeliveryMode('send');
    } else {
      setPatientContactTouched(false);
      setFillerContactTouched(false);
      create.reset();
    }
    // We deliberately exclude `create` from the deps so we don't reset
    // on every render of the mutation hook. (No exhaustive-deps lint
    // configured at the workspace level, so no disable directive
    // needed — note this intent in case the plugin is ever added.)
  }, [open, defaultEmail, defaultPhone, defaultFormId, forms]);

  // Adopt the deployment's channel as soon as it is known, and keep the
  // selection honest if the user has not chosen one yet.
  const { data: channelData } = useMagicLinkChannels();
  const deliverableChannels = channelData?.channels ?? [];
  useEffect(() => {
    const first = deliverableChannels[0];
    if (first !== undefined && (channel === '' || !deliverableChannels.includes(channel))) {
      setChannel(first);
    }
  }, [deliverableChannels, channel]);

  /**
   * The patient the server says this contact already belongs to
   * (intakeform-preview-fjhe). Present only on a 409, which is raised BEFORE
   * anything is minted — so this is a question, not a notification about a
   * link that already exists.
   */
  const duplicateSuspect =
    create.error?.code === 'duplicate_patient_suspect'
      ? ((
          create.error.details as
            { existing?: { displayName?: string; mrn?: string; birthDate?: string } } | undefined
        )?.existing ?? {})
      : null;
  /**
   * True when we can tell the clinician NOTHING about the record they are
   * being asked to merge into (intakeform-preview-1dba).
   *
   * Patients created before a name was required have no name and usually no
   * MRN, so the prompt could render "an existing patient record" and a single
   * button — asking for a judgement while showing no evidence. A prompt like
   * that is dismissed, and dismissing it becomes the habit that defeats the
   * sibling prompt, which is the one that matters.
   */
  const suspectIsAnonymous =
    duplicateSuspect !== null &&
    duplicateSuspect.displayName === undefined &&
    duplicateSuspect.mrn === undefined &&
    duplicateSuspect.birthDate === undefined;

  const patientNameValid = patientName.trim().length > 0;
  const patientNameMissing = patientNameTouched && !patientNameValid;
  const patientContactValid = email.trim().length > 0 || phone.trim().length > 0;
  const fillerContactValid =
    !fillerEnabled || fillerEmail.trim().length > 0 || fillerPhone.trim().length > 0;

  // Showing the link needs no sender, so "show only" can issue before — or
  // without — the channels endpoint ever answering.
  const sends = deliveryMode !== 'show';
  const shows = deliveryMode !== 'send';

  // `channel === ''` means we have not heard back yet. Issuing a SEND then
  // would post a channel we invented, which is the whole bug this replaced.
  const canIssue =
    formId !== '' &&
    (!sends || channel !== '') &&
    !create.isPending &&
    patientNameValid &&
    patientContactValid &&
    fillerContactValid;

  const onIssue = (confirmReusePatient = false): void => {
    // Force the FormError to show even if the user clicks submit
    // without ever blurring the fields.
    setPatientContactTouched(true);
    setPatientNameTouched(true);
    if (fillerEnabled) setFillerContactTouched(true);
    // Load-bearing beyond the early return: when a send is wanted `canIssue`
    // includes `channel !== ''`, and TypeScript narrows through it, which is
    // what keeps the body below from posting a channel we never heard from
    // the server.
    if (!canIssue) return;

    const patient: { displayName: string; email?: string; phone?: string; mrn?: string } = {
      displayName: patientName.trim(),
    };
    if (email.trim().length > 0) patient.email = email.trim();
    if (phone.trim().length > 0) patient.phone = phone.trim();
    if (mrn.trim().length > 0) patient.mrn = mrn.trim();

    const body: Parameters<typeof create.mutate>[0] = {
      questionnaireId: formId,
      patient,
      ...(confirmReusePatient ? { confirmReusePatient: true } : {}),
    };
    // Omitting `channel` is how the API is told to send nothing; it is not a
    // channel value, so there is nothing to invent here (lmxg).
    if (sends && channel !== '') body.channel = channel;
    if (shows) body.display = true;
    if (fillerEnabled) {
      const filler: NonNullable<typeof body.filler> = {
        relationshipCode: fillerRelationshipCode,
      };
      if (fillerEmail.trim().length > 0) filler.email = fillerEmail.trim();
      if (fillerPhone.trim().length > 0) filler.phone = fillerPhone.trim();
      if (fillerName.trim().length > 0) filler.displayName = fillerName.trim();
      body.filler = filler;
    }

    // Per-link TTL override (xv2): only set when the admin actually
    // typed a value. Blank → resolver uses per-form > per-org > env.
    if (ttlHoursOverride.trim().length > 0) {
      const h = Number(ttlHoursOverride);
      if (Number.isFinite(h) && h > 0) {
        body.ttlSeconds = Math.round(h * 3600);
      }
    }

    create.mutate(body, {
      onSuccess: () => {
        toast({ title: 'Magic link issued', variant: 'success' });
      },
      onError: (err) => {
        toast({
          title: 'Could not issue magic link',
          description: err.message,
          variant: 'danger',
        });
      },
    });
  };

  const onCopy = async (): Promise<void> => {
    if (displayedLink === undefined) return;
    try {
      await navigator.clipboard.writeText(displayedLink);
      toast({
        title: 'Link copied',
        description: 'Paste it into the device the patient will use.',
        variant: 'success',
      });
    } catch {
      // Clipboard API rejects under non-secure contexts or when the
      // page lacks focus. Fall back to selecting the input so the user
      // can copy with the keyboard — better than failing silently.
      const input = dialogRef.current?.querySelector<HTMLInputElement>('input[data-dev-link]');
      input?.select();
      toast({
        title: 'Clipboard unavailable',
        description: 'Use Cmd+C / Ctrl+C to copy the highlighted link.',
        variant: 'warning',
      });
    }
  };

  const patientErrorFindings: Finding[] =
    patientContactTouched && !patientContactValid ? [CONTACT_REQUIRED] : [];
  const fillerErrorFindings: Finding[] =
    fillerEnabled && fillerContactTouched && !fillerContactValid ? [CONTACT_REQUIRED] : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content width="32rem" ref={dialogRef}>
        <Dialog.Title>Issue a magic link</Dialog.Title>

        {link === undefined ? (
          <Dialog.Body>
            <label>
              <span>Form</span>
              <select
                value={formId}
                onChange={(e) => setFormId(e.currentTarget.value)}
                style={{ width: '100%' }}
              >
                {forms.length === 0 ? <option value="">No forms available</option> : null}
                {forms.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.title} (v{f.version})
                  </option>
                ))}
              </select>
            </label>

            {/* ── Patient section ─────────────────────────────────────
             * Always rendered, regardless of delivery channel: the API
             * needs an identifier on the patient row whether the link
             * is delivered via console / email / SMS (in-9bn). */}
            <section
              aria-labelledby="patient-section-heading"
              aria-describedby="patient-contact-help"
              data-patient-section
              className="modal-section"
            >
              <header className="modal-section-header">
                <h3 id="patient-section-heading" className="modal-section-title">
                  Patient
                </h3>
                <p id="patient-contact-help" className="modal-section-hint">
                  Email or phone is required — provide at least one.
                </p>
              </header>

              <div data-patient-contact className="modal-field-grid">
                {/* Required, and first, because it is the only field that says
                    WHICH patient this is (intakeform-preview-1dba). Email and
                    phone belong to a parent and are shared between siblings,
                    so without a name a second child matches the first child's
                    record and the link opens the wrong chart. */}
                <label>
                  <span>Patient name</span>
                  <input
                    type="text"
                    name="patient-name"
                    data-patient-name
                    value={patientName}
                    onChange={(e) => setPatientName(e.currentTarget.value)}
                    onBlur={() => setPatientNameTouched(true)}
                    placeholder="Ada Lovelace"
                    required
                    maxLength={200}
                    aria-invalid={patientNameMissing ? true : undefined}
                    style={{ width: '100%' }}
                  />
                </label>
                <label>
                  <span>Patient email</span>
                  <input
                    type="email"
                    name="patient-email"
                    data-patient-email
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    onBlur={() => setPatientContactTouched(true)}
                    placeholder="patient@example.com"
                    aria-invalid={patientErrorFindings.length > 0 ? true : undefined}
                    style={{ width: '100%' }}
                  />
                </label>
                <label>
                  <span>Patient phone</span>
                  <input
                    type="tel"
                    name="patient-phone"
                    data-patient-phone
                    value={phone}
                    onChange={(e) => setPhone(e.currentTarget.value)}
                    onBlur={() => setPatientContactTouched(true)}
                    placeholder="+15555550123"
                    aria-invalid={patientErrorFindings.length > 0 ? true : undefined}
                    style={{ width: '100%' }}
                  />
                </label>
                <label>
                  <span>Medical record number (optional)</span>
                  <input
                    type="text"
                    name="patient-mrn"
                    data-patient-mrn
                    value={mrn}
                    onChange={(e) => setMrn(e.currentTarget.value)}
                    placeholder="MRN00421"
                    maxLength={64}
                    style={{ width: '100%' }}
                  />
                </label>
                <FormError findings={patientErrorFindings} id="patient-contact-error" />
              </div>

              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 8,
                }}
              >
                <input
                  type="checkbox"
                  name="filler-enabled"
                  data-filler-toggle
                  checked={fillerEnabled}
                  onChange={(e) => {
                    setFillerEnabled(e.currentTarget.checked);
                    if (!e.currentTarget.checked) setFillerContactTouched(false);
                  }}
                />
                <span>Send to a caregiver instead of the patient</span>
              </label>

              {fillerEnabled ? (
                <div
                  aria-describedby="filler-contact-help"
                  data-filler-contact
                  className="modal-field-grid"
                  style={{ marginTop: 8 }}
                >
                  <p id="filler-contact-help" className="modal-section-hint">
                    Caregiver email or phone is required.
                  </p>
                  <label>
                    <span>Relationship</span>
                    <select
                      name="filler-relationship"
                      value={fillerRelationshipCode}
                      onChange={(e) =>
                        setFillerRelationshipCode(e.currentTarget.value as RelationshipCodeValue)
                      }
                      style={{ width: '100%' }}
                    >
                      {RELATIONSHIP_CODES.map((code) => (
                        <option key={code} value={code}>
                          {RelationshipCode[code].display}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Caregiver name</span>
                    <input
                      type="text"
                      name="filler-name"
                      value={fillerName}
                      onChange={(e) => setFillerName(e.currentTarget.value)}
                      placeholder="Alex Doe"
                      style={{ width: '100%' }}
                    />
                  </label>
                  <label>
                    <span>Caregiver email</span>
                    <input
                      type="email"
                      name="filler-email"
                      data-filler-email
                      value={fillerEmail}
                      onChange={(e) => setFillerEmail(e.currentTarget.value)}
                      onBlur={() => setFillerContactTouched(true)}
                      placeholder="caregiver@example.com"
                      aria-invalid={fillerErrorFindings.length > 0 ? true : undefined}
                      style={{ width: '100%' }}
                    />
                  </label>
                  <label>
                    <span>Caregiver phone</span>
                    <input
                      type="tel"
                      name="filler-phone"
                      data-filler-phone
                      value={fillerPhone}
                      onChange={(e) => setFillerPhone(e.currentTarget.value)}
                      onBlur={() => setFillerContactTouched(true)}
                      placeholder="+15555550123"
                      aria-invalid={fillerErrorFindings.length > 0 ? true : undefined}
                      style={{ width: '100%' }}
                    />
                  </label>
                  <FormError findings={fillerErrorFindings} id="filler-contact-error" />
                </div>
              ) : null}
            </section>

            {/* ── Delivery section ────────────────────────────────────
             * Channel is a dropdown (was radios pre-in-9bn) — three
             * options is at the threshold; the dropdown reads cleaner
             * next to the section heading and frees horizontal room
             * for narrower viewports. Channel ONLY changes how the
             * link is delivered; the patient identifier above is
             * always required regardless. */}
            <section
              aria-labelledby="delivery-section-heading"
              data-delivery-section
              className="modal-section"
            >
              <header className="modal-section-header">
                <h3 id="delivery-section-heading" className="modal-section-title">
                  Delivery
                </h3>
                <p className="modal-section-hint">
                  How the magic link reaches the recipient. Only the channels this deployment is
                  configured to deliver on are offered.
                </p>
              </header>

              {/* ── What to do with the link (intakeform-preview-lmxg) ──
               * Showing it on screen needs no sender, so it is offered on
               * every deployment regardless of what the channels endpoint
               * reports. Sending stays the default: for a link issued
               * remotely nobody is at the desk to read a screen, and
               * rendering a live credential there would be exposure that
               * buys nothing. */}
              <fieldset data-delivery-mode style={{ border: 0, margin: 0, padding: 0 }}>
                <legend className="sr-only">What to do with the link</legend>
                {(['send', 'send-and-show', 'show'] as const).map((mode) => (
                  <label
                    key={mode}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}
                  >
                    <input
                      type="radio"
                      name="delivery-mode"
                      value={mode}
                      data-testid={`delivery-mode-${mode}`}
                      checked={deliveryMode === mode}
                      onChange={() => setDeliveryMode(mode)}
                    />
                    <span>{DELIVERY_MODE_LABELS[mode]}</span>
                  </label>
                ))}
                {shows ? (
                  <p className="modal-section-hint" style={{ marginTop: 4 }}>
                    The link is the patient&apos;s credential. It appears on this screen only, is
                    never written to a log, and disappears when you close this dialog.
                  </p>
                ) : null}
              </fieldset>

              <label style={{ display: sends ? undefined : 'none' }}>
                <span className="sr-only">Channel</span>
                <select
                  name="channel"
                  data-channel
                  value={channel}
                  onChange={(e) => setChannel(e.currentTarget.value as Channel)}
                  style={{ width: '100%' }}
                  aria-label="Delivery channel"
                >
                  {/* Rendered from what the server reports it can deliver, so
                      the list cannot offer a channel that will fail. Production
                      never shows `console` because ConsoleSender refuses to
                      construct under NODE_ENV=production — the link it would
                      write to the log IS the credential
                      (intakeform-preview-8vz). The desk's real need, a link in
                      hand, is intakeform-preview-lmxg and not a channel. */}
                  {channel === '' ? <option value="">Loading…</option> : null}
                  {deliverableChannels.map((c) => (
                    <option key={c} value={c}>
                      {CHANNEL_LABELS[c]}
                    </option>
                  ))}
                </select>
              </label>
            </section>

            <section>
              <header style={{ marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
                  Link expiry override (optional)
                </h3>
                <p className="muted" style={{ margin: '4px 0 0', fontSize: 'var(--text-xs)' }}>
                  Hours from now. Leave blank to use the form's expiry, the clinic's default, or the
                  server default — in that order.
                </p>
              </header>
              <label>
                <span className="sr-only">Link expiry hours</span>
                <input
                  type="number"
                  data-testid="ttl-hours-override"
                  min={0.0167}
                  max={720}
                  step={0.25}
                  value={ttlHoursOverride}
                  onChange={(e) => setTtlHoursOverride(e.currentTarget.value)}
                  placeholder="e.g. 24"
                  style={{ width: '100%' }}
                />
              </label>
            </section>

            {duplicateSuspect !== null ? (
              <div
                className="stale-banner"
                role="alert"
                data-warning="duplicate-patient"
                style={{ marginBottom: 0 }}
              >
                <strong>This contact is already on file for another patient.</strong>{' '}
                <span>
                  {duplicateSuspect.displayName !== undefined
                    ? `It belongs to "${duplicateSuspect.displayName}". `
                    : 'It belongs to an existing patient record. '}
                  {duplicateSuspect.birthDate !== undefined
                    ? `Date of birth on file: ${duplicateSuspect.birthDate}. `
                    : ''}
                  {duplicateSuspect.mrn !== undefined
                    ? `(MRN on file: ${duplicateSuspect.mrn}) `
                    : ''}
                  {suspectIsAnonymous
                    ? 'We hold no name or date of birth for it, so this cannot be checked from here — open the patient in the EMR before confirming. '
                    : ''}
                  Siblings often share a parent&apos;s email or phone. If this is a different
                  person, cancel and issue against the right patient — do not change the contact
                  details.
                </span>
                <div className="modal-actions" style={{ marginTop: 8 }}>
                  <Button type="button" onClick={() => onIssue(true)} data-confirm-reuse>
                    Yes — same patient, issue it
                  </Button>
                </div>
              </div>
            ) : create.error ? (
              <div className="error-banner">{create.error.message}</div>
            ) : null}
          </Dialog.Body>
        ) : (
          <Dialog.Body>
            <p style={{ margin: 0 }}>
              <span className="badge badge-info badge-dot">Issued</span>{' '}
              <span className="muted">Expires {new Date(link.expiresAt).toLocaleString()}</span>
            </p>
            {/* The send half, reported plainly. A failed copy no longer kills
             * the link when it was also shown (lmxg amending 6f7), so the
             * clinician has to be told the difference between "they have it
             * and a copy is in their inbox" and "they have it and nothing was
             * sent" — otherwise they assume the inbox. */}
            {link.delivery !== undefined ? (
              link.delivery.ok ? (
                <p className="muted" style={{ margin: 0 }} data-delivery-ok>
                  Sent by {link.delivery.channel}.
                </p>
              ) : (
                <div className="stale-banner" role="alert" data-delivery-failed>
                  <strong>The {link.delivery.channel} copy did not go out.</strong>{' '}
                  <span>
                    {displayedLink !== undefined
                      ? 'The link below still works — the patient has it from this screen. Follow up if they need a copy sent.'
                      : 'Check the address and issue a new link.'}
                  </span>
                </div>
              )
            ) : null}

            {/* The QR is the point of the desk flow (intakeform-preview-mkiz):
             * it moves the link to the patient's own device with no
             * intermediate copy. Gated on `link.link` — the response key that
             * exists only because the clinician asked to see the link — and
             * deliberately NOT on `displayedLink`, which falls back to the
             * dev-only devLink. */}
            {link.link !== undefined ? (
              <div
                data-magic-link-qr-panel
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
              >
                <MagicLinkQr link={link.link} />
                {/* Real text, not just an aria-label: the QR is useless to
                 * anyone who cannot point a camera at it, so the alternative
                 * has to be visible and stated rather than implied. The link
                 * itself is in the field below, already labelled. */}
                <p className="modal-section-hint" style={{ margin: 0, textAlign: 'center' }}>
                  Point the patient&apos;s camera at this code. The same link is in the box below if
                  you need to copy it onto a waiting-room device.
                </p>
              </div>
            ) : null}

            {displayedLink !== undefined ? (
              <>
                <p style={{ margin: 0 }} className="muted">
                  {link.link !== undefined
                    ? 'Show this to the patient, or copy it onto a waiting-room device.'
                    : 'Dev link — copy it and open in a patient browser to test.'}
                </p>
                <div className="copy-row">
                  <input
                    type="text"
                    readOnly
                    data-dev-link
                    value={displayedLink}
                    onFocus={(e) => e.currentTarget.select()}
                    aria-label="Magic link URL"
                  />
                  <Button type="button" onClick={() => void onCopy()}>
                    Copy
                  </Button>
                </div>
              </>
            ) : (
              <p className="muted" style={{ margin: 0 }}>
                The link was delivered to the patient and is not shown here. Re-issue with
                &ldquo;show it here&rdquo; if you need it at the desk.
              </p>
            )}
          </Dialog.Body>
        )}

        <Dialog.Footer>
          {link === undefined ? (
            <>
              <Dialog.Close asChild>
                <Button type="button" variant="secondary" disabled={create.isPending}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                type="button"
                onClick={() => onIssue()}
                disabled={!canIssue}
                data-issue-link
                aria-disabled={!canIssue}
              >
                {create.isPending ? 'Issuing…' : 'Issue link'}
              </Button>
            </>
          ) : (
            <Dialog.Close asChild>
              <Button type="button">Done</Button>
            </Dialog.Close>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}
